/* MUMS redesign — Phase 3b shop interactions (progressive enhancement).
   Pure add-on: never rewrites theme markup, degrades to native behaviour if anything
   is missing. Three features:
     1. PDP live "per 100 g" line (price ÷ weight), flashes on variant change.
     2. In-dropdown search inside the Flavor facet (desktop + mobile), client-side.
     3. Mobile sticky add-to-bag bar mirroring the buy rail.
   Scoped by body template class so it costs nothing on other pages. */
(function () {

  'use strict';

  /* Locale-aware storefront URLs.
   *
   * Shopify serves an additional locale from a subfolder — Shopify.routes.root is '/th/' on the
   * Thai storefront and '/' on the primary one. Every endpoint below used to be root-relative,
   * so on /th they hit the ENGLISH storefront and returned English titles and option names: the
   * cart drawer, quick view, predictive search, Pairs Well With and the bestsellers rail all
   * silently ignored the locale. Translating products would have appeared to do nothing.
   */
  function mumsUrl(path) {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root + String(path).replace(/^\/+/, '');
  }

  /* Localised UI strings — bootstrapped as window.mumsStrings by layout/theme.liquid from the
   * locale files (mums.ui / mums.size). English fallback if the bootstrap is missing.
   * Occasion names ('Regular', 'Large', …) stay English as LOGIC keys; occLabel() is display only. */
  function S(key, fallback) {
    var s = window.mumsStrings;
    return (s && s[key]) || fallback;
  }
  function occLabel(occ) { return occ ? S('occ_' + occ.toLowerCase(), occ) : ''; }

  /* Candy sold by the piece (heavy pieces, 2026-10-28): size values are piece counts ("3 pcs"),
   * not weights. The tier comes from the value's position — MUST match
   * snippets/product-variant-options.liquid — and the grams from the variant's real weight. */
  var PCS_TIERS = ['Taste', 'Regular', 'Sharing', 'Party'];
  function piecesOf(str) {
    var m = String(str || '').trim().toLowerCase().match(/^(\d+)\s*pcs$/);
    return m ? parseInt(m[1], 10) : 0;
  }
  function pcsLabel(n, grams) {
    return grams > 0 ? S('pcs_sub', '[n] pieces · ~[g]g').replace('[n]', n).replace('[g]', Math.round(grams))
                     : S('pcs', '[n] pieces').replace('[n]', n);
  }

  var doc = document;
  var onReady = function (fn) {
    if (doc.readyState !== 'loading') fn();
    else doc.addEventListener('DOMContentLoaded', fn);
  };

  /* ---------- shared money helpers ---------- */
  // Derive the store's money format from an already-rendered price node so we never
  // guess the currency symbol, placement, or decimal count.
  function readMoneyFormat(sampleEl) {
    // Matches the store's money_format (฿{{amount}} = two decimals). A 0-decimal fallback made the
    // quick view print "฿199" next to the PDP's "฿199.00" whenever the sample had no digits.
    var fallback = { symbol: '฿', suffix: false, decimals: 2 };
    if (!sampleEl) return fallback;
    var txt = (sampleEl.textContent || '').replace(/\s+/g, ' ').trim();
    var m = txt.match(/[\d][\d.,]*/);
    if (!m) return fallback;
    var num = m[0];
    var idx = txt.indexOf(num);
    var before = txt.slice(0, idx).replace(/[\d\s]/g, '').trim();
    var after = txt.slice(idx + num.length).replace(/[\d\s]/g, '').trim();
    var dec = 0;
    var decMatch = num.match(/[.,](\d{2})$/);
    if (decMatch) dec = 2;
    return {
      symbol: before || after || '฿',
      suffix: !before,
      decimals: dec
    };
  }
  function formatMoney(cents, fmt) {
    var major = cents / 100;
    var n = major.toLocaleString('en-US', {
      minimumFractionDigits: fmt.decimals,
      maximumFractionDigits: fmt.decimals
    });
    return fmt.suffix ? n + ' ' + fmt.symbol : fmt.symbol + n;
  }
  // Pull a weight in grams out of any variant title / option string: "500g", "1 kg", "1kg".
  function gramsFrom(str) {
    if (!str) return 0;
    var s = String(str).toLowerCase().replace(/\s+/g, '');
    var kg = s.match(/([\d.]+)kg/);
    if (kg) return Math.round(parseFloat(kg[1]) * 1000);
    var g = s.match(/([\d.]+)g\b/) || s.match(/([\d.]+)g/);
    if (g) return Math.round(parseFloat(g[1]));
    return 0;
  }

  /* ================================================================
     1 · PDP per-100g line + variant flash  +  3 · mobile sticky bar
     ================================================================ */
  function initProduct() {
    if (!doc.body.classList.contains('template-product')) return;
    var info = doc.querySelector('.product-info-container');
    if (!info) return;

    var priceBlock = info.querySelector('.product-price');
    var radios = info.querySelector('variant-radios');
    var form = info.querySelector('form[data-type="add-to-cart-form"]');
    var submit = info.querySelector('.product-form__submit');
    var titleEl = info.querySelector('.product-title');

    // Variant catalogue (present whenever the product has real variants).
    var variants = [];
    try {
      var vnode = info.querySelector('[data-all-variants-no-high]');
      if (vnode) variants = JSON.parse(vnode.textContent);
    } catch (e) { variants = []; }

    var moneyFmt = readMoneyFormat(priceBlock && priceBlock.querySelector('.price-item'));

    // ---- per-100g node ----
    // The theme replaces the price block's innerHTML on every variant change
    // (Section Rendering), which wipes our injected node — so re-ensure it each update.
    function ensurePer100() {
      if (!priceBlock) return null;
      var node = priceBlock.querySelector('.mums-per100');
      if (node) return node; // already present — don't move it (avoid observer churn)
      node = doc.createElement('div');
      node.className = 'mums-per100';
      // inject as a SIBLING right after .price, inside the .mums-priceline row. The
      // theme's .price is flex-direction:column (stacks its children), so a child node
      // wraps below the amount; placed in .mums-priceline (forced flex-direction:row in
      // mums-shop.css) it sits inline, baseline-aligned with the amount.
      var priceEl = priceBlock.querySelector('.price');
      if (priceEl && priceEl.parentNode) { priceEl.after(node); }
      else { priceBlock.appendChild(node); }
      return node;
    }

    // ---- keep the "Add to Bag — {price}" label priced per variant ----
    // toggleAddButton() in global.js resets the label to variantStrings.addToCart on
    // variant change; we re-apply the priced label here (runs after that reset).
    function syncAtcLabel(cents) {
      if (!submit || submit.disabled || !(cents > 0)) return;
      var lbl = submit.querySelector('.button__label');
      if (!lbl) return;
      var desired = S('add_to_bag', 'Add to Bag') + ' — ' + formatMoney(cents, moneyFmt);
      if (lbl.textContent.trim() !== desired) {
        lbl.textContent = desired;
        lbl.setAttribute('data-label', desired);
      }
    }

    function currentVariant() {
      if (!variants.length) return null;
      // Primary: the hidden id input the theme keeps in sync.
      var idInput = form && form.querySelector('input[name="id"]');
      if (idInput && idInput.value) {
        var byId = variants.filter(function (v) { return String(v.id) === String(idInput.value); })[0];
        if (byId) return byId;
      }
      // Fallback: match on checked option radios (DOM order == option position).
      if (radios) {
        var checked = [].slice.call(radios.querySelectorAll('input[type="radio"]:checked'))
          .map(function (i) { return i.value; });
        if (checked.length) {
          var byOpt = variants.filter(function (v) {
            return v.options.length === checked.length &&
              v.options.every(function (o, i) { return o === checked[i]; });
          })[0];
          if (byOpt) return byOpt;
        }
      }
      return variants.filter(function (v) { return v.available; })[0] || variants[0];
    }

    function priceOf(v) {
      if (v && typeof v.price === 'number') return v.price;
      // Single-variant / default product: read the rendered price.
      var item = priceBlock && priceBlock.querySelector('.price-item--sale, .price-item--regular');
      if (item) {
        var m = (item.textContent || '').match(/[\d][\d.,]*/);
        if (m) return Math.round(parseFloat(m[0].replace(/,/g, '')) * 100);
      }
      return 0;
    }

    function update(flash) {
      var v = currentVariant();
      var grams = gramsFrom(v ? v.title : '') ||
        gramsFrom(v && v.options ? v.options.join(' ') : '') ||
        (v && piecesOf(weightOptionOf(v)) && v.weight > 0 ? v.weight : 0);
      var cents = priceOf(v);
      var per100 = ensurePer100();
      if (per100) {
        if (grams > 0 && cents > 0) {
          var per = Math.round(cents / grams * 100);
          var perTxt = formatMoney(per, moneyFmt) + ' / 100g';
          if (per100.textContent !== perTxt) per100.textContent = perTxt;
          per100.hidden = false;
          if (flash) {
            per100.classList.remove('mums-per100--flash');
            // reflow to restart the animation
            void per100.offsetWidth;
            per100.classList.add('mums-per100--flash');
          }
        } else {
          per100.hidden = true;
        }
      }
      syncAtcLabel(cents);
      syncSticky(v, cents);
    }

    // ---- mobile sticky add-to-bag bar (mockup .m-satc: mini size row + buy row) ----
    // occasion label from a size option value — MUST match product-variant-options.liquid.
    function occasionFrom(sizeVal, idx) {
      var k = String(sizeVal).toLowerCase();
      if (piecesOf(k)) return PCS_TIERS[idx] || '';
      if (k.indexOf('1kg') >= 0 || k.indexOf('1000') >= 0) return 'Party';
      if (k.indexOf('500') >= 0) return 'Sharing';
      if (k.indexOf('200') >= 0) return 'Regular';
      if (k.indexOf('100') >= 0) return 'Taste';
      if (k === 'small') return 'Small';   // mix sizes (match product-variant-options.liquid)
      if (k === 'medium') return 'Medium';
      if (k === 'large') return 'Large';
      if (k === 'party') return 'Party';     // mix Party = 2 × Large
      return ''; // non-standard weight → no occasion, show the raw size
    }
    // the size-bearing option value of a variant (e.g. "100g", "1kg", or a piece count "3 pcs").
    function weightOptionOf(v) {
      if (!v || !v.options) return '';
      for (var i = 0; i < v.options.length; i++) {
        if (gramsFrom(v.options[i]) > 0 || piecesOf(v.options[i])) return String(v.options[i]);
      }
      return '';
    }

    var sticky = null, stickyPrice = null, stickyTiles = [];
    if (submit && titleEl) {
      sticky = doc.createElement('div');
      sticky.className = 'mums-stickybuy';
      sticky.setAttribute('aria-hidden', 'false');

      // size row — one mini-tile per size variant, driving the MAIN variant radios (single
      // source of truth) so the sticky + main tiles + price stay in two-way sync.
      if (radios && variants.length > 1) {
        var sizes = doc.createElement('div');
        sizes.className = 'mums-stickybuy__sizes';
        variants.forEach(function (v, vi) {
          var sizeVal = weightOptionOf(v);
          if (!sizeVal) return;
          var occ = occasionFrom(sizeVal, vi);
          var tile = doc.createElement('button');
          tile.type = 'button';
          tile.className = 'mums-stickybuy__ss';
          tile.setAttribute('data-size-value', sizeVal);
          var o = doc.createElement('span'); o.className = 'mums-stickybuy__ss-o';
          o.textContent = occLabel(occ) || sizeVal;
          var g = doc.createElement('span'); g.className = 'mums-stickybuy__ss-g';
          g.textContent = !occ ? '' : piecesOf(sizeVal) ? pcsLabel(piecesOf(sizeVal), 0) : sizeVal;
          tile.appendChild(o); tile.appendChild(g);
          tile.addEventListener('click', function () {
            var rs = radios.querySelectorAll('input[type="radio"]');
            for (var i = 0; i < rs.length; i++) {
              if (rs[i].value === sizeVal) { if (!rs[i].checked) rs[i].click(); break; }
            }
          });
          stickyTiles.push(tile);
          sizes.appendChild(tile);
        });
        if (stickyTiles.length) sticky.appendChild(sizes);
      }

      // buy row — name + price·size + Add to Bag button
      var buy = doc.createElement('div');
      buy.className = 'mums-stickybuy__buy';
      var meta = doc.createElement('div');
      meta.className = 'mums-stickybuy__meta';
      var nm = doc.createElement('span');
      nm.className = 'mums-stickybuy__name';
      nm.textContent = titleEl.textContent.trim();
      stickyPrice = doc.createElement('span');
      stickyPrice.className = 'mums-stickybuy__price';
      meta.appendChild(nm);
      meta.appendChild(stickyPrice);
      var btn = doc.createElement('button');
      btn.type = 'button';
      btn.className = 'mums-stickybuy__cta';
      btn.textContent = S('add_to_bag', 'Add to Bag');
      btn.addEventListener('click', function () {
        // Sold-out candy: the sticky CTA hands off to the "Notify me" form (snippets/mums-notify-me.liquid).
        var nf = doc.querySelector('#mums-notify .mums-notify__form');
        if (submit.disabled && nf) {
          nf.scrollIntoView({ behavior: 'smooth', block: 'center' });
          var ni = nf.querySelector('input[type="email"]');
          if (ni) setTimeout(function () { ni.focus({ preventScroll: true }); }, 400);
          return;
        }
        if (submit.disabled) return;
        submit.click();
      });
      buy.appendChild(meta);
      buy.appendChild(btn);
      sticky.appendChild(buy);
      doc.body.appendChild(sticky);
    }
    function sizeLabel(v) {
      if (!v) return '';
      var t = (v.title || '').trim();
      if (t && t.toLowerCase() !== 'default title') return t;
      // Fall back to the weight-bearing option (e.g. "100g", "1kg").
      if (v.options && v.options.length) {
        for (var i = 0; i < v.options.length; i++) {
          if (gramsFrom(v.options[i]) > 0) return String(v.options[i]).trim();
        }
      }
      return '';
    }
    function syncSticky(v, cents) {
      if (!sticky) return;
      var soldOut = submit && submit.disabled;
      var canNotify = soldOut && !!doc.querySelector('#mums-notify .mums-notify__form');
      sticky.querySelector('.mums-stickybuy__cta').textContent = canNotify ? S('notify', 'Notify me')
        : soldOut ? S('sold_out', 'Sold Out') : S('add_to_bag', 'Add to Bag');
      sticky.classList.toggle('is-soldout', !!soldOut && !canNotify);
      if (stickyPrice) {
        stickyPrice.textContent = '';
        if (cents > 0) {
          // mockup .satc-buy .p: bold price first, muted " · size" after
          var b = doc.createElement('b');
          b.textContent = formatMoney(cents, moneyFmt);
          stickyPrice.appendChild(b);
          var size = sizeLabel(v);
          if (size) stickyPrice.appendChild(doc.createTextNode(' · ' + size));
        }
      }
      // reflect the selected size on the mini-tiles (two-way sync with the main tiles)
      if (stickyTiles.length) {
        var sel = weightOptionOf(v);
        stickyTiles.forEach(function (t) {
          t.classList.toggle('is-on', t.getAttribute('data-size-value') === sel);
        });
      }
    }

    // Wrap update() so our own DOM writes (per-100g node, label) don't retrigger the
    // price observer into an infinite loop: disconnect around each run.
    var priceObserver = null;
    function runUpdate(flash) {
      if (priceObserver) priceObserver.disconnect();
      try { update(flash); } catch (e) { /* never block */ }
      if (priceObserver && priceBlock) {
        priceObserver.observe(priceBlock, { childList: true, subtree: true, characterData: true });
      }
    }

    runUpdate(false);

    /* ---- anti-flash on size change (BUG: transient "Sold out"/disabled paint) ----
       On a variant change the theme calls toggleAddButton(true, …) and, mid Section-
       Rendering fetch (~400ms), can paint a disabled / sold-out / unavailable state
       for a frame before the real state lands. While switching between AVAILABLE
       variants we hold the button enabled with the priced "Add to Bag — …" label and
       suppress that transient; a genuinely sold-out target is left to paint normally.
       Same disconnect-bracket pattern as the price observer so our writes don't loop. */
    var atcObserver = null;
    var atcHold = null; // { label, variant, cents }
    function variantFromRadios() {
      if (!variants.length || !radios) return null;
      var checked = [].slice.call(radios.querySelectorAll('input[type="radio"]:checked'))
        .map(function (i) { return i.value; });
      if (!checked.length) return null;
      return variants.filter(function (v) {
        return v.options.length === checked.length &&
          v.options.every(function (o, i) { return o === checked[i]; });
      })[0] || null;
    }
    function enforceHold() {
      if (!atcHold || !submit) return;
      if (submit.hasAttribute('disabled')) submit.removeAttribute('disabled');
      var lbl = submit.querySelector('.button__label');
      if (lbl && lbl.textContent.trim() !== atcHold.label) {
        lbl.textContent = atcHold.label;
        lbl.setAttribute('data-label', atcHold.label);
      }
    }
    function runEnforce() {
      if (!atcHold) return;
      if (atcObserver) atcObserver.disconnect();
      try { enforceHold(); } catch (e) {}
      try { syncSticky(atcHold.variant, atcHold.cents); } catch (e) {}
      if (atcObserver && submit) {
        atcObserver.observe(submit, { attributes: true, attributeFilter: ['disabled'], childList: true, subtree: true, characterData: true });
      }
    }
    if (submit && 'MutationObserver' in window) {
      atcObserver = new MutationObserver(function () { if (atcHold) runEnforce(); });
    }
    function beginHold() {
      var v = variantFromRadios() || currentVariant();
      // Only suppress the transient for an available target; genuine sold-out paints.
      if (!v || !v.available) { atcHold = null; if (atcObserver) atcObserver.disconnect(); return; }
      var cents = priceOf(v);
      if (!(cents > 0)) { atcHold = null; if (atcObserver) atcObserver.disconnect(); return; }
      atcHold = { label: S('add_to_bag', 'Add to Bag') + ' — ' + formatMoney(cents, moneyFmt), variant: v, cents: cents };
      runEnforce();
      clearTimeout(beginHold._t);
      beginHold._t = setTimeout(function () {
        atcHold = null;
        if (atcObserver) atcObserver.disconnect();
        runUpdate(true); // re-sync to the real, settled state
      }, 900);
    }

    // React to variant changes: theme re-renders .product-price, and radios fire change.
    if (radios) radios.addEventListener('change', function () {
      try { beginHold(); } catch (e) {}
      setTimeout(function () { runUpdate(true); }, 40);
    });
    if (priceBlock && 'MutationObserver' in window) {
      priceObserver = new MutationObserver(function () { runUpdate(true); });
      priceObserver.observe(priceBlock, { childList: true, subtree: true, characterData: true });
    }

    /* ---- default the size to "Regular" (200g) — the Popular tier leads (Andreas) ----
       Only when the shopper hasn't chosen a variant via the URL (?variant=). Clicks the REAL
       200g radio so the theme's own Section Rendering keeps price / ATC / cart / URL correct.
       No-op if the product has no available 200g variant. Runs once, after all observers are
       wired so the anti-flash hold suppresses any transient state during its fetch. */
    (function selectDefaultSize() {
      if (!radios) return;
      try { if (new URLSearchParams(location.search).get('variant')) return; } catch (e) { return; }
      var rs = radios.querySelectorAll('input[type="radio"]');
      for (var i = 0; i < rs.length; i++) {
        // candy leads with "Regular" (200g); mixes lead with "Large" — both are the Popular tier
        var occ = occasionFrom(rs[i].value, i);
        if ((occ === 'Regular' || occ === 'Large') && !rs[i].disabled) {
          if (!rs[i].checked) {
            rs[i].click();
            // paint the 200g price/labels immediately so there's no ฿99→฿198 flash while the
            // theme's Section Rendering fetch is in flight (it re-confirms the same price).
            try {
              var v = currentVariant();
              var item = priceBlock && priceBlock.querySelector('.price-item--sale, .price-item--regular');
              if (v && item && v.price > 0) item.textContent = formatMoney(v.price, moneyFmt);
              runUpdate(false);
            } catch (e) {}
          }
          break;
        }
      }
    })();
  }

  /* ================================================================
     2 · In-dropdown search for the Flavor facet (desktop + mobile)
     ================================================================ */
  function facetLabel(el) {
    var s = el.querySelector('.facets__summary-title, .mobile-facets__filter-label, legend, summary');
    return (s ? s.textContent : el.textContent || '').toLowerCase();
  }
  function isSearchableFacet(label, optionCount) {
    return /flavou?r/.test(label) || optionCount >= 12;
  }
  function wireFacetSearch(panel, items) {
    if (!panel || panel.querySelector('.mums-facet-search')) return;
    var wrap = doc.createElement('div');
    wrap.className = 'mums-facet-search';
    wrap.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.8-3.8"/></svg>';
    var input = doc.createElement('input');
    input.type = 'search';
    input.placeholder = 'Search flavors…';
    input.setAttribute('aria-label', 'Search flavors');
    wrap.appendChild(input);
    panel.insertBefore(wrap, panel.firstChild);
    // Prevent the disclosure from closing / navigating on interaction.
    ['click', 'keydown', 'keyup', 'input'].forEach(function (evt) {
      input.addEventListener(evt, function (e) { e.stopPropagation(); });
    });
    input.addEventListener('input', function () {
      var q = input.value.trim().toLowerCase();
      items.forEach(function (li) {
        var t = (li.textContent || '').toLowerCase();
        li.style.display = (!q || t.indexOf(q) !== -1) ? '' : 'none';
      });
    });
  }
  // The theme renders each option label as one text node "Name (N)". Split the
  // trailing count into its own span so CSS can right-align it (reference .dd-opt .cnt).
  // Idempotent + leaves textContent searchable for the flavour filter above.
  function splitFacetCounts(root) {
    [].slice.call(root.querySelectorAll('.facet-checkbox__label')).forEach(function (lbl) {
      if (lbl.querySelector('.mums-cnt')) return;                 // already split
      if (lbl.childNodes.length !== 1 || lbl.firstChild.nodeType !== 3) return; // only plain text
      var m = lbl.textContent.match(/^([\s\S]*?)\s*\((\d[\d,]*)\)\s*$/);
      if (!m) return;
      lbl.textContent = m[1];
      var cnt = doc.createElement('span');
      cnt.className = 'mums-cnt';
      cnt.textContent = m[2];
      lbl.appendChild(cnt);
    });
  }
  function initFacets() {
    if (!doc.body.classList.contains('template-collection') &&
        !doc.body.classList.contains('template-search')) return;

    try { splitFacetCounts(doc); } catch (e) { /* counts stay inline */ }

    // Desktop dropdown disclosures.
    [].slice.call(doc.querySelectorAll('.facets__disclosure')).forEach(function (d) {
      var panel = d.querySelector('.facets__display');
      var items = panel ? [].slice.call(panel.querySelectorAll('.facets__item')) : [];
      if (panel && isSearchableFacet(facetLabel(d), items.length)) wireFacetSearch(panel, items);
    });
    // Mobile bottom-sheet submenus.
    [].slice.call(doc.querySelectorAll('.mobile-facets__details')).forEach(function (d) {
      var panel = d.querySelector('.mobile-facets__submenu');
      var items = panel ? [].slice.call(panel.querySelectorAll('.mobile-facets__item, .facets__item, li')) : [];
      if (panel && isSearchableFacet(facetLabel(d), items.length)) wireFacetSearch(panel, items);
    });
  }

  /* ================================================================
     2b · Horizontal dropdown filter BAR (desktop, collection only).
     Enhances the theme's vertical facet <form>: re-lays the real <details>
     disclosures as click-popovers, relocates Sort to the right, mirrors the
     active-value count into a badge. Native form submit + chips are untouched;
     checkbox change still auto-submits via the theme's own listener.
     ================================================================ */
  var barObserver = null;
  function initFilterBar() {
    if (!doc.body.classList.contains('template-collection') &&
        !doc.body.classList.contains('template-search')) return;
    var bar = doc.querySelector('.facets--desktop');
    if (!bar) return;
    var form = bar.querySelector('#FacetFiltersForm');

    // Relocate Sort (its own <form>, kept intact) to the right of the bar.
    var sort = doc.querySelector('.collection-top .facets__sort');
    if (sort && sort.parentNode !== bar) bar.appendChild(sort);

    // Move the active-chips row to its OWN full-width row BELOW the merged bar
    // (was inline in the pill row → crowded the Flavors/Dietary/Sort row).
    var chips = doc.querySelector('.facets__top');
    var container = bar.closest('.facets-container--vertical') || bar;
    if (chips && container.parentNode && chips !== container.nextElementSibling) {
      container.after(chips);
    }

    // Start every disclosure closed (Liquid renders them `open`).
    [].forEach.call(bar.querySelectorAll('.facets__disclosure'), function (d) { d.open = false; });

    // Popover toggle via delegation (survives Section-Rendering DOM swaps).
    if (!bar.dataset.mumsBarWired) {
      bar.dataset.mumsBarWired = '1';
      bar.addEventListener('click', function (e) {
        var sum = e.target.closest('.facets__summary');
        if (!sum || !bar.contains(sum)) return;
        e.preventDefault();
        var d = sum.closest('.facets__disclosure');
        if (!d) return;
        var willOpen = !d.open;
        closeAllDisclosures(bar);
        if (willOpen) d.open = true;
      });
      doc.addEventListener('click', function (e) {
        if (!e.target.closest('.facets--desktop .facets__disclosure')) closeAllDisclosures(bar);
      });
      doc.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeAllDisclosures(bar);
      });
    }

    refreshFacetCounts(bar);

    // Re-decorate after the theme re-renders the facet fragment on submit.
    if ('MutationObserver' in window && form && !barObserver) {
      barObserver = new MutationObserver(function () {
        if (barObserver._t) clearTimeout(barObserver._t);
        barObserver._t = setTimeout(function () {
          barObserver.disconnect();
          try { refreshFacetCounts(bar); } catch (e) {}
          try { initFacets(); } catch (e) {}
          barObserver.observe(form, { childList: true, subtree: true });
        }, 120);
      });
      barObserver.observe(form, { childList: true, subtree: true });
    }
  }
  function closeAllDisclosures(bar) {
    [].forEach.call(bar.querySelectorAll('.facets__disclosure[open]'), function (d) { d.open = false; });
  }
  function refreshFacetCounts(bar) {
    [].forEach.call(bar.querySelectorAll('.facets__disclosure'), function (d) {
      var sum = d.querySelector('.facets__summary');
      if (!sum) return;
      var badge = sum.querySelector('.mums-facet-count');
      if (!badge) {
        badge = doc.createElement('span');
        badge.className = 'mums-facet-count';
        var title = sum.querySelector('.facets__summary-title');
        if (title) title.appendChild(badge); else sum.insertBefore(badge, sum.firstChild);
      }
      var n = d.querySelectorAll('.facets__display input[type="checkbox"]:checked').length;
      badge.textContent = n ? String(n) : '';
    });
  }

  /* ================================================================
     2c · MOBILE two-pill filter bar (collection, <=749px)
     Surfaces the theme's real mobile facets (menu-drawer) as the reference
     bottom-sheet: relocates the "Filter" trigger + an injected "Sort" pill into a
     sticky bar, and keeps the footer button label live as "Show N Products".
     No duplicate control: the desktop bar is hidden on mobile via CSS.
     ================================================================ */
  function readProductCount() {
    var el = doc.getElementById('ProductCountDesktop') || doc.getElementById('ProductCount') ||
      doc.querySelector('.facets__product-count-text');
    if (!el) return null;
    var m = (el.textContent || '').replace(/\s+/g, ' ').match(/\d[\d,]*/);
    return m ? m[0] : null;
  }
  function updateMobileApplyLabel() {
    var lbl = doc.querySelector('#FacetFiltersFormMobile .mobile-facets__footer__button .button__label');
    if (!lbl) return;
    var n = readProductCount();
    var txt = n != null ? ('Show ' + n + ' Products') : 'Show Products';
    if (lbl.textContent.trim() !== txt) { lbl.textContent = txt; lbl.setAttribute('data-label', txt); }
  }
  // The mobile sheet is FLAT: every filter group renders expanded. Liquid emits
  // `open`, but re-assert it here so cached/older fragments still show values
  // (not headings-only) after a Section-Rendering swap.
  function expandMobileFacetGroups() {
    [].forEach.call(doc.querySelectorAll('#FacetFiltersFormMobile .mobile-facets__details'), function (d) {
      if (d.tagName === 'DETAILS' && !d.open) d.open = true;
    });
  }
  // the mobile sheet's filter groups, in order, minus Type (Type = the category pills now)
  // and price — mirrors the desktop bar (Flavors, Dietary).
  function mobileFilterGroups() {
    var out = [];
    // Exclude by filter PARAM, not translated label text (labels localize → English matches break
    // the Thai storefront). Type = the category pills on COLLECTION only; /search keeps Type.
    var onCollection = doc.body.classList.contains('template-collection') &&
                       !doc.body.classList.contains('template-search');
    // Our Mixes is the exception: its category pills navigate to OTHER collections, so the Type
    // filter (Sour/Sweet/Pick & Mix) is the only way to refine the mixes — keep it as a pill there.
    var isMixes = doc.body.classList.contains('collection-our-mixes');
    [].forEach.call(doc.querySelectorAll('.mobile-facets__details'), function (d) {
      var h = d.querySelector('.mobile-facets__summary, summary, legend');
      if (!h) return;
      var fInput = d.querySelector('input[name^="filter."]');
      var param = fInput ? fInput.getAttribute('name') : '';
      if (!param) return;                                          // Sort group (no filter param) — has its own pill
      if (param.indexOf('price') !== -1) return;                   // price range — not a pill
      if (onCollection && !isMixes && param.indexOf('taste_category') !== -1) return; // Type == category pills (except Mixes)
      var label = (h.textContent || '').trim().split('\n')[0].trim();
      if (!label) return;
      var n = d.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked').length;
      out.push({ el: d, label: label, count: n });
    });
    return out;
  }
  // Measure the sticky header stack (announcement + header) bottom so the sheet can start
  // BELOW it — the announcement height lives in a section-scoped CSS var that isn't readable
  // from the sheet, so we measure the live rects instead. Scrolled state (announcement gone)
  // and top state (announcement + header) both resolve correctly.
  function setSheetTopOffset() {
    var b = 0;
    var sels = ['.announcement-bar', '.announcement-bar-section', '.shopify-section-header',
                '.header-wrapper', 'sticky-header', '.section-header'];
    sels.forEach(function (sel) {
      [].forEach.call(doc.querySelectorAll(sel), function (el) {
        var r = el.getBoundingClientRect();
        if (r.height && r.top < 90 && r.bottom > b && r.bottom < 320) b = r.bottom;
      });
    });
    if (b > 0) doc.documentElement.style.setProperty('--mums-sheet-top', Math.round(b) + 'px');
  }
  // open the theme sheet and ISOLATE it to a single group (Flavors OR Dietary) — each pill opens
  // only its own filter list, not the unified stack. body[data-mfgroup] drives the CSS isolation
  // (set on body so it survives the facet AJAX re-render, which swaps the sheet DOM).
  function openMobileSheetToGroup(groupEl) {
    var wrapper = doc.querySelector('.mobile-facets__wrapper');
    if (!wrapper) return;
    var det = wrapper.querySelector('details');
    var sum = wrapper.querySelector('summary');
    // derive the group key from the tapped group's filter param
    var inp = groupEl && groupEl.querySelector('input[name^="filter."]');
    var pname = inp ? (inp.getAttribute('name') || '') : '';
    var key = pname.indexOf('flavors') > -1 ? 'flavors'
            : (pname.indexOf('dietary') > -1 ? 'dietary'
            : (pname.indexOf('taste_category') > -1 ? 'type' : ''));
    if (key) { doc.body.setAttribute('data-mfgroup', key); } else { doc.body.removeAttribute('data-mfgroup'); }
    setSheetTopOffset();
    if (det && !det.open) { if (sum) { sum.click(); } else { det.open = true; } }
    expandMobileFacetGroups();
    setTimeout(function () {
      setSheetTopOffset(); // re-measure once the open animation has settled
      // isolated view scrolls to its own top (the single visible group)
      var scroller = doc.querySelector('.mobile-facets__inner');
      if (scroller) { try { scroller.scrollTo({ top: 0 }); } catch (e) { scroller.scrollTop = 0; } }
    }, 80);
  }
  function initMobileFilterBar() {
    if (!doc.body.classList.contains('template-collection') &&
        !doc.body.classList.contains('template-search')) return;
    var wrapper = doc.querySelector('.mobile-facets__wrapper');
    if (!wrapper) return;
    expandMobileFacetGroups();
    if (doc.querySelector('.mums-mfbar')) { updateMobileApplyLabel(); return; }

    var bar = doc.createElement('div');
    bar.className = 'mums-mfbar';
    wrapper.parentNode.insertBefore(bar, wrapper);
    bar.appendChild(wrapper); // keep the sheet's <details> in the DOM (its summary is hidden via CSS)

    // One pill per filter group (Flavors, Dietary) — each opens the bottom sheet to that group.
    mobileFilterGroups().forEach(function (g) {
      var pill = doc.createElement('button');
      pill.type = 'button';
      pill.className = 'mums-mfpill';
      pill.appendChild(doc.createTextNode(g.label));
      pill.insertAdjacentHTML('beforeend', '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>');
      if (g.count) { var b = doc.createElement('span'); b.className = 'mums-mfpill__n'; b.textContent = g.count; pill.appendChild(b); }
      pill.addEventListener('click', function () { openMobileSheetToGroup(g.el); });
      bar.appendChild(pill);
    });

    // Sort pill — a native <select> mirroring the theme's in-drawer sort options.
    // On change we navigate with the new sort_by (preserving active filter params) —
    // a full, dependency-free path that always renders the correct sorted grid.
    var msel = doc.getElementById('SortBy-mobile');
    if (msel && msel.options.length) {
      var sortWrap = doc.createElement('div');
      sortWrap.className = 'mums-msort';
      var pre = doc.createElement('span');
      pre.className = 'mums-msort__label';
      pre.textContent = S('sort_label', 'Sort:');
      var sel = doc.createElement('select');
      sel.className = 'mums-msort__select';
      sel.setAttribute('aria-label', 'Sort by');
      [].slice.call(msel.options).forEach(function (o) {
        var opt = doc.createElement('option');
        opt.value = o.value;
        opt.textContent = (o.getAttribute('data-short') || o.textContent).trim(); // short label fits the phone pill
        if (o.selected) opt.selected = true;
        sel.appendChild(opt);
      });
      sel.value = msel.value;
      sel.addEventListener('change', function () {
        var params = new URLSearchParams(window.location.search);
        params.set('sort_by', sel.value);
        window.location.search = params.toString();
      });
      sortWrap.appendChild(pre);
      sortWrap.appendChild(sel);
      sortWrap.insertAdjacentHTML('beforeend', '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>');
      bar.appendChild(sortWrap);
    }
    updateMobileApplyLabel();
  }

  /* ---- Add-to-cart feedback: toast ("Added ✓ <product>") + cart-icon bump ----
     Hooks window.fetch so it catches EVERY add path (PDP form, quick-add, drawer upsell),
     since they all POST to /cart/add or /cart/add.js. Toast is suppressed when the cart
     drawer is already open (it shows its own feedback). */
  function initCartFeedback() {
    var toastEl = null, toastTimer = null;
    function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
    function showToast(title) {
      if (!toastEl) {
        toastEl = doc.createElement('div');
        toastEl.className = 'mums-toast';
        toastEl.setAttribute('role', 'status');
        toastEl.setAttribute('aria-live', 'polite');
        doc.body.appendChild(toastEl);
      }
      toastEl.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M8.5 12.5l2.5 2.5 4.5-5"/></svg><span class="t"><b>Added</b> ' + esc(title) + '</span>';
      // land it exactly where the search/login panels drop in: (announcement bottom) + header
      // height + 12 — matches on any page + scroll state (announcement clamps to 0 when scrolled off).
      var ann = doc.querySelector('.announcement-bar');
      var annB = ann ? Math.max(0, ann.getBoundingClientRect().bottom) : 0;
      var hh = parseInt(getComputedStyle(doc.documentElement).getPropertyValue('--header-height'), 10) || 64;
      toastEl.style.top = Math.max(56, Math.round(annB + hh + 26)) + 'px';
      // align the toast's right edge with the cart icon's right edge
      var cartIcon = doc.querySelector('#cart-icon-bubble, .header__icon--cart');
      if (cartIcon) {
        var cr = cartIcon.getBoundingClientRect();
        toastEl.style.right = Math.max(8, Math.round(window.innerWidth - cr.right)) + 'px';
      }
      // reflow so the transition replays even on back-to-back adds
      void toastEl.offsetWidth;
      toastEl.classList.add('show');
      clearTimeout(toastTimer);
      toastTimer = setTimeout(function () { toastEl.classList.remove('show'); }, 2400);
    }
    function bumpCart() {
      doc.querySelectorAll('#cart-icon-bubble, #cart-icon-bubble-burger, .header__icon--cart').forEach(function (el) {
        el.classList.remove('mums-cart-bump'); void el.offsetWidth; el.classList.add('mums-cart-bump');
      });
    }
    if (window.__mumsCartFeedback) return;
    window.__mumsCartFeedback = true;
    // Resolve the added product's title (for the toast) from the fresh cart, then show feedback.
    // Suppressed when the cart drawer is open (it shows its own), but still bump the icon.
    function onAdd(variantId) {
      if (doc.querySelector('cart-drawer.active')) { bumpCart(); return; }
      fetch(mumsUrl('cart.js'), { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var items = cart.items || [];
          var item = null;
          for (var i = 0; i < items.length; i++) { if (String(items[i].variant_id) === String(variantId)) { item = items[i]; break; } }
          if (!item) item = items[items.length - 1];
          showToast((item && (item.product_title || item.title)) || 'Item');
          bumpCart();
        }).catch(function () { bumpCart(); });
    }
    // PREFERRED: the theme's native cart pub/sub event — no global fetch patch, no per-fetch cost.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (evt) {
        if (evt && evt.source === 'product-form') onAdd(evt.productVariantId);   // an add (not a qty change)
      });
    } else if (window.fetch) {
      // FALLBACK only if the theme's pub/sub isn't present: a lean /cart/add hook.
      var origFetch = window.fetch;
      window.fetch = function (input, init) {
        var url = (typeof input === 'string') ? input : (input && input.url) || '';
        var method = (init && init.method) || 'GET';
        var isAdd = /\/cart\/add(\.js)?(\?|$)/.test(url) && /post/i.test(method);
        var ret = origFetch.apply(this, arguments);
        if (isAdd && ret && ret.then) ret.then(function (res) { if (res && res.ok) onAdd(null); }).catch(function () {});
        return ret;
      };
    }
  }

  onReady(function () {
    try { initCartFeedback(); } catch (e) { /* feedback is optional */ }
    try { initProduct(); } catch (e) { /* never block the page */ }
    try { initFacets(); } catch (e) { /* native facets still work */ }
    try { initFilterBar(); } catch (e) { /* native facets still work */ }
    try { initMobileFilterBar(); } catch (e) { /* native mobile facets still work */ }
    // Search & Discovery replaces the grid + facets via the Section Rendering API on
    // filter/sort; re-attach the flavour search when the facets DOM is swapped.
    var host = doc.getElementById('ProductGridContainer') || doc.querySelector('#main-collection-product-grid, .main-collection-product-grid');
    if (host && 'MutationObserver' in window) {
      var deb;
      new MutationObserver(function () {
        clearTimeout(deb);
        deb = setTimeout(function () {
          try { initFacets(); } catch (e) {}
          var bar = doc.querySelector('.facets--desktop');
          if (bar) { try { refreshFacetCounts(bar); } catch (e) {} }
          try { initMobileFilterBar(); } catch (e) {}
          try { updateMobileApplyLabel(); } catch (e) {}
        }, 120);
      }).observe(host, { childList: true, subtree: true });
    }
  });

  /* ============================================================
     CART DRAWER — contextual "frequently added" upsell
     Fills [data-mums-upsell] with a Shopify related-product pick based on the cart's anchor
     item, skipping anything already in the bag. Falls back: related → bestsellers → merchant
     pick. Re-runs on every drawer re-render. Progressive: no shell = nothing happens.
     ============================================================ */
  function upsellMoneyFmt() {
    return readMoneyFormat(
      doc.querySelector('.cart-drawer .totals__subtotal-value, .cart-drawer .cart-item__final-price, .cart-drawer .cart-item__price-wrapper')
    );
  }
  function upsellFirstVariant(p) {
    if (!p || !p.variants) return null;
    for (var i = 0; i < p.variants.length; i++) if (p.variants[i].available) return p.variants[i];
    return null;
  }
  function upsellSizedImage(url) {
    if (!url || typeof url !== 'string') return '';
    if (url.indexOf('//') === 0) url = 'https:' + url;
    if (/width=|_\d+x\d*\./.test(url)) return url; // already sized by Liquid / CDN
    return url.replace(/(\.(?:png|jpe?g|webp|gif))(\?|$)/i, '_120x$1$2');
  }
  function upsellPick(products, exclude) {
    if (!products) return null;
    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      if (!p || exclude[String(p.id)]) continue;
      var v = upsellFirstVariant(p);
      if (v) return { p: p, v: v };
    }
    return null;
  }
  function upsellFetch(url) {
    return fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }
  function initCartUpsell() {
    var el = doc.querySelector('[data-mums-upsell]');
    if (!el || el.dataset.mumsState === 'busy' || el.dataset.mumsState === 'ready') return;
    el.dataset.mumsState = 'busy';
    var anchor = el.getAttribute('data-anchor');
    var exclude = {};
    (el.getAttribute('data-exclude') || '').split(',').filter(Boolean).forEach(function (id) { exclude[String(id)] = true; });

    function paint(pick) {
      if (!pick) { el.hidden = true; el.dataset.mumsState = 'empty'; return; }
      var p = pick.p, v = pick.v;
      // image can be a string URL (/recommendations, /products.json) or an object {src}
      // (/collections/x/products.json) — normalise to a string.
      var rawImg = p.featured_image || (p.images && p.images[0]) || '';
      var raw = (typeof rawImg === 'string') ? rawImg : (rawImg && (rawImg.src || rawImg.url)) || '';
      var media = el.querySelector('.mums-upsell__media');
      var img = upsellSizedImage(raw);
      if (media) media.innerHTML = img ? '<img src="' + img + '" alt="" width="52" height="52" loading="lazy">' : '';
      var title = el.querySelector('.mums-upsell__title');
      if (title) title.textContent = S('upsell_title', 'Add [title]?').replace('[title]', p.title || '');
      var note = el.querySelector('.mums-upsell__note');
      if (note) {
        // price is cents (number, from /recommendations) OR a "99.00" major-unit string (from
        // /collections/x/products.json) — normalise to cents. Prefix the weight/size (variant title).
        var cents = (typeof v.price === 'string') ? Math.round(parseFloat(v.price) * 100) : v.price;
        var priceStr = formatMoney(cents, upsellMoneyFmt());
        var weight = v.title || v.public_title || v.option1 || '';
        note.textContent = (weight && weight.toLowerCase() !== 'default title') ? (weight + ' · ' + priceStr) : priceStr;
      }
      var btn = el.querySelector('[data-mums-upsell-add]');
      if (btn) { btn.setAttribute('data-variant-id', v.id); btn.disabled = false; }
      el.hidden = false;
      el.dataset.mumsState = 'ready';
    }

    // last resort: the merchant-picked product baked into the shell
    function merchantPick() {
      var fbEl = el.querySelector('[data-mums-upsell-fallback]');
      if (fbEl) {
        try {
          var fb = JSON.parse(fbEl.textContent);
          if (fb && !exclude[String(fb.product_id)]) {
            return paint({ p: { title: fb.title, featured_image: fb.featured_image, id: fb.product_id }, v: { id: fb.id, price: fb.price } });
          }
        } catch (e) {}
      }
      paint(null);
    }
    // second: Shopify related products (needs a cart anchor)
    function relatedThenMerchant() {
      if (!anchor) return merchantPick();
      upsellFetch(mumsUrl('recommendations/products.json?product_id=') + encodeURIComponent(anchor) + '&intent=related&limit=10')
        .then(function (rec) {
          var pick = upsellPick(rec && rec.products, exclude);
          if (pick) paint(pick); else merchantPick();
        });
    }
    // FIRST: Bestsellers collection (Andreas curates it) → related → merchant pick.
    // Skips anything already in the bag; keeps looking down the chain until it finds one.
    upsellFetch(mumsUrl('collections/bestsellers/products.json?limit=20')).then(function (bs) {
      var pick = upsellPick(bs && bs.products, exclude);
      if (pick) paint(pick); else relatedThenMerchant();
    });
  }
  // Add → native drawer refresh via the theme's renderContents() + Section Rendering API
  doc.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-mums-upsell-add]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    var vid = btn.getAttribute('data-variant-id');
    if (!vid) return;
    btn.disabled = true;
    var drawer = doc.querySelector('cart-drawer');
    var canRender = drawer && typeof drawer.renderContents === 'function';
    var body = { id: Number(vid), quantity: 1 };
    if (canRender) { body.sections = 'cart-drawer,cart-icon-bubble,cart-icon-bubble-burger'; body.sections_url = window.location.pathname; }
    fetch(mumsUrl('cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); }).then(function (state) {
      if (canRender && state && state.sections) drawer.renderContents(state);
      else window.location = mumsUrl('cart');
    }).catch(function () { btn.disabled = false; });
  });
  /* ===================== QUICK VIEW =====================
     The card icon (.pc-quick[data-qv]) opens a lightweight PDP popup instead of navigating away:
     photo + dietary badge, weight pills (the product's variants), live price, Add to Bag, and a
     "View full details" link. Add reuses the cart-drawer renderContents() path (auto-opens the
     drawer), same as the upsell. Prefills from the card DOM, then fetches /products/<h>.js for
     variants so the popup appears instantly. */
  var qvModal = null, qvVariants = [], qvSelected = null;
  function qvFmt() {
    try {
      // prefer a product-card price (renders "฿99.00") over the cart drawer, whose subtotal uses
      // the "THB {{amount}}" format — reading that gave the popup THB instead of ฿.
      // A sold-out card renders "Sold Out" in .pc-price, so take the first card that actually
      // shows a number — otherwise one sold-out product at the top of the grid set the format.
      var sample = null, cards = doc.querySelectorAll('.pc-price');
      for (var qi = 0; qi < cards.length; qi++) {
        if (/\d/.test(cards[qi].textContent || '')) { sample = cards[qi]; break; }
      }
      var f = readMoneyFormat(sample || doc.querySelector('.cart-drawer .totals__subtotal-value, .cart-drawer .cart-item__final-price'));
      if (f && f.symbol) return f;
    } catch (e) {}
    return { decimals: 2, suffix: false, symbol: '฿' };
  }
  function qvEsc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
  function qvBuild() {
    var w = doc.createElement('div');
    w.className = 'mums-qv';
    w.setAttribute('hidden', '');
    w.innerHTML =
      '<div class="mums-qv__scrim" data-qv-close></div>' +
      '<div class="mums-qv__card" role="dialog" aria-modal="true" aria-label="Quick view">' +
        '<button class="mums-qv__x" type="button" data-qv-close aria-label="Close">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
        '</button>' +
        '<div class="mums-qv__media"><span class="mums-qv__badges"></span><img class="mums-qv__img" alt="" width="480" height="480"></div>' +
        '<div class="mums-qv__body">' +
          '<div class="mums-qv__cat"></div>' +
          '<h3 class="mums-qv__name"></h3>' +
          '<p class="mums-qv__desc"></p>' +
          '<div class="mums-qv__wtlabel">' + qvEsc(S('choose_size', 'Choose a size')) + '</div>' +
          '<div class="mums-qv__weights"><span class="mums-qv__muted">' + qvEsc(S('loading', 'Loading…')) + '</span></div>' +
          '<button class="mums-qv__add" type="button" disabled>' + qvEsc(S('add_to_bag', 'Add to Bag')) + '</button>' +
          '<a class="mums-qv__more" href="#">' + qvEsc(S('view_details', 'View full details')) + '</a>' +
        '</div>' +
      '</div>';
    doc.body.appendChild(w);
    w.addEventListener('click', function (e) { if (e.target.closest('[data-qv-close]')) qvClose(); });
    w.querySelector('.mums-qv__weights').addEventListener('click', function (e) {
      var p = e.target.closest('.mums-qv__tile'); if (p && !p.disabled) qvSelect(p.getAttribute('data-vid'));
    });
    w.querySelector('.mums-qv__add').addEventListener('click', qvAdd);
    return w;
  }
  // weight → occasion tile label + Popular flag — MUST mirror snippets/product-variant-options.liquid.
  function qvOccasion(title, idx, grams) {
    var k = String(title).toLowerCase();
    var n = piecesOf(k);
    if (n) return { occ: PCS_TIERS[idx] || '', pop: idx === 1, sub: pcsLabel(n, grams) };
    if (k.indexOf('1kg') >= 0 || k.indexOf('1000') >= 0) return { occ: 'Party' };
    if (k.indexOf('500') >= 0) return { occ: 'Sharing' };
    if (k.indexOf('200') >= 0) return { occ: 'Regular', pop: true };
    if (k.indexOf('100') >= 0) return { occ: 'Taste' };
    if (k === 'small') return { occ: 'Small', sub: '~250g' };
    if (k === 'medium') return { occ: 'Medium', sub: '~420g' };
    if (k === 'large') return { occ: 'Large', pop: true, sub: '~590g' };
    return { occ: '' };
  }
  var qvAddLabel = S('add_to_bag', 'Add to Bag');
  function qvSelect(vid) {
    qvSelected = vid;
    var v = null, i;
    for (i = 0; i < qvVariants.length; i++) if (String(qvVariants[i].id) === String(vid)) v = qvVariants[i];
    [].forEach.call(qvModal.querySelectorAll('.mums-qv__tile'), function (p) {
      p.classList.toggle('is-sel', String(p.getAttribute('data-vid')) === String(vid));
    });
    var add = qvModal.querySelector('.mums-qv__add');
    if (v) { qvAddLabel = S('add_to_bag', 'Add to Bag') + ' · ' + formatMoney(v.price, qvFmt()); add.textContent = qvAddLabel; add.disabled = false; }
    else { qvAddLabel = S('add_to_bag', 'Add to Bag'); add.textContent = qvAddLabel; add.disabled = true; }
  }
  function qvRenderWeights() {
    var box = qvModal.querySelector('.mums-qv__weights'), i, fmt = qvFmt();
    if (!qvVariants.length) { box.innerHTML = '<span class="mums-qv__muted">' + qvEsc(S('no_sizes', 'No sizes available')) + '</span>'; return; }
    box.innerHTML = qvVariants.map(function (v, vi) {
      var o = qvOccasion(v.title, vi, v.weight);
      var tag = o.pop ? '<span class="mums-qv__tag">' + qvEsc(S('popular', 'Popular')) + '</span>' : '';
      var occ, sub;
      if (o.occ) { occ = '<span class="mums-qv__occ">' + qvEsc(occLabel(o.occ)) + '</span>'; sub = '<span class="mums-qv__gr">' + qvEsc(o.sub || v.title) + '</span>'; }
      else { occ = '<span class="mums-qv__occ">' + qvEsc(v.title) + '</span>'; sub = ''; }
      var pr = '<span class="mums-qv__pr">' + formatMoney(v.price, fmt) + '</span>';
      return '<button type="button" class="mums-qv__tile' + (v.available ? '' : ' is-oos') + '" data-vid="' + v.id + '"' + (v.available ? '' : ' disabled') + '>' + tag + occ + sub + pr + '</button>';
    }).join('');
    // default to the Popular tier (Regular 200g / mix Large), else first available — matches the PDP.
    var pick = null;
    for (i = 0; i < qvVariants.length; i++) { var oo = qvOccasion(qvVariants[i].title, i, qvVariants[i].weight); if (oo.pop && qvVariants[i].available) { pick = qvVariants[i]; break; } }
    if (!pick) for (i = 0; i < qvVariants.length; i++) if (qvVariants[i].available) { pick = qvVariants[i]; break; }
    if (pick) qvSelect(pick.id);
  }
  function qvOpen(btn) {
    if (!qvModal) qvModal = qvBuild();
    var card = btn.closest('.pc');
    var handle = btn.getAttribute('data-qv');
    var url = btn.getAttribute('data-qv-url') || (mumsUrl('products/' + handle));
    // CLEAN url for the .js fetch — on search, product.url carries ?_pos/_sid tracking params, so
    // "<url>.js" returned the product HTML page (invalid JSON → "No sizes available"). The handle
    // is always clean; fall back to stripping the query if a handle is somehow absent.
    var jsUrl = handle ? (mumsUrl('products/' + handle)) : url.split('?')[0].split('#')[0];
    var img = card && card.querySelector('.pc-field img');
    var name = card && card.querySelector('.pc-name');
    var cat = card && card.querySelector('.pc-cat');
    var badge = card && card.querySelector('.pc-badge');
    var imgEl = qvModal.querySelector('.mums-qv__img');
    imgEl.src = img ? (img.currentSrc || img.src) : '';
    imgEl.alt = name ? name.textContent : '';
    qvModal.querySelector('.mums-qv__name').textContent = name ? name.textContent : '';
    var catEl = qvModal.querySelector('.mums-qv__cat');
    catEl.textContent = cat ? cat.textContent : '';
    catEl.style.display = (cat && cat.textContent.trim()) ? '' : 'none';
    qvModal.querySelector('.mums-qv__badges').innerHTML = badge ? ('<span class="mums-qv__badge">' + qvEsc(badge.textContent) + '</span>') : '';
    qvModal.querySelector('.mums-qv__more').setAttribute('href', url);
    qvModal.querySelector('.mums-qv__weights').innerHTML = '<span class="mums-qv__muted">' + qvEsc(S('loading', 'Loading…')) + '</span>';
    qvAddLabel = S('add_to_bag', 'Add to Bag');
    var addBtn = qvModal.querySelector('.mums-qv__add');
    addBtn.textContent = qvAddLabel; addBtn.disabled = true;
    qvVariants = []; qvSelected = null;
    qvModal.removeAttribute('hidden');
    doc.documentElement.classList.add('mums-qv-open');
    fetch(jsUrl + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (p) {
        qvVariants = p.variants || [];
        if (p.featured_image) imgEl.src = (String(p.featured_image).indexOf('//') === 0 ? 'https:' : '') + p.featured_image;
        // product description (plain text) — fills the desktop card; hidden on mobile via CSS
        var tmp = doc.createElement('div'); tmp.innerHTML = p.description || '';
        qvModal.querySelector('.mums-qv__desc').textContent = (tmp.textContent || '').replace(/\s+/g, ' ').trim();
        qvRenderWeights();
      })
      .catch(function () { qvModal.querySelector('.mums-qv__weights').innerHTML = '<span class="mums-qv__muted">' + qvEsc(S('sizes_failed', 'Couldn’t load sizes — open the product page.')) + '</span>'; });
  }
  function qvClose() { if (qvModal) { qvModal.setAttribute('hidden', ''); doc.documentElement.classList.remove('mums-qv-open'); } }
  // Safe cart refresh + open. The theme's own drawer.renderContents() iterates a HARDCODED section
  // list incl. cart-icon-bubble-burger, whose element is absent on desktop → it throws on the null
  // target BEFORE it opens the drawer. Replicate it but skip missing targets, then open.
  function qvRenderCart(drawer, state) {
    (drawer.getSectionsToRender ? drawer.getSectionsToRender() : []).forEach(function (section) {
      var el = section.selector ? doc.querySelector(section.selector) : doc.getElementById(section.id);
      var html = state.sections && state.sections[section.id];
      if (!el || html == null) return;
      try {
        var frag = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector || '.shopify-section');
        if (frag) el.innerHTML = frag.innerHTML;
      } catch (e) {}
    });
    // 2026-10-15: `is-empty` sits on the <cart-drawer> HOST, not on .drawer__inner — clearing only
    // the inner left `.drawer.is-empty .drawer__footer{display:none}` hiding the Checkout button
    // after the first add of a session. Same fix as assets/cart-drawer.js renderContents().
    drawer.classList.remove('is-empty');
    var inner = drawer.querySelector('.drawer__inner');
    if (inner) inner.classList.remove('is-empty');
    setTimeout(function () { try { drawer.open(); } catch (e) {} });
  }
  function qvAdd() {
    if (!qvSelected) return;
    var add = qvModal.querySelector('.mums-qv__add');
    add.disabled = true; add.textContent = S('adding', 'Adding…');
    var drawer = doc.querySelector('cart-drawer');
    var canRender = drawer && typeof drawer.renderContents === 'function';
    var body = { id: Number(qvSelected), quantity: 1 };
    if (canRender) { body.sections = 'cart-drawer,cart-icon-bubble,cart-icon-bubble-burger'; body.sections_url = window.location.pathname; }
    fetch(mumsUrl('cart/add.js'), { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(body) })
      .then(function (r) { return r.json(); })
      .then(function (state) {
        add.disabled = false; add.textContent = qvAddLabel;
        qvClose();
        if (canRender && state && state.sections) qvRenderCart(drawer, state); // safe render + open
        else if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-view' });
        else window.location = mumsUrl('cart');
      })
      .catch(function () { add.disabled = false; add.textContent = qvAddLabel; });
  }
  // Mixologist Picks (homepage): the card's + adds its variant straight to the cart, then refreshes
  // and opens the drawer the same safe way the quick-view popup does.
  function initPicksAdd() {
    doc.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('.mp-add[data-variant]');
      if (!btn || btn.getAttribute('aria-busy') === 'true') return;
      e.preventDefault(); e.stopPropagation();
      btn.setAttribute('aria-busy', 'true');
      var drawer = doc.querySelector('cart-drawer');
      var canRender = drawer && typeof drawer.renderContents === 'function';
      var body = { id: Number(btn.getAttribute('data-variant')), quantity: 1 };
      if (canRender) { body.sections = 'cart-drawer,cart-icon-bubble,cart-icon-bubble-burger'; body.sections_url = window.location.pathname; }
      fetch(mumsUrl('cart/add.js'), { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(body) })
        .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
        .then(function (state) {
          btn.removeAttribute('aria-busy');
          btn.classList.add('is-added');
          setTimeout(function () { btn.classList.remove('is-added'); }, 1600);
          if (canRender && state && state.sections) qvRenderCart(drawer, state);
          else if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mixologist-picks' });
          else window.location = mumsUrl('cart');
        })
        .catch(function () { btn.removeAttribute('aria-busy'); });
    }, true);
  }
  function initQuickView() {
    // capture phase + stopPropagation so the card's overlay link (.pc-link) never fires
    doc.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('.pc-quick[data-qv]');
      if (!btn) return;
      e.preventDefault(); e.stopPropagation();
      qvOpen(btn);
    }, true);
    doc.addEventListener('keydown', function (e) { if (e.key === 'Escape') qvClose(); });
  }

  // Collection filter bar: on desktop it is lifted onto the category-pill row and must stay
  // see-through there, but once it sticks under the header it needs a solid background (it was
  // floating over the product photos). Flag the stuck state; CSS paints the background.
  function initStickyBarFlag() {
    var bar = doc.querySelector('.template-collection .collection-wrapper--vertical .facets-container--vertical');
    if (!bar) return;
    var ticking = false;
    function update() {
      ticking = false;
      var top = parseFloat(getComputedStyle(bar).top) || 0;
      var stuck = window.scrollY > 0 && bar.getBoundingClientRect().top <= top + 1;
      bar.classList.toggle('mums-bar-stuck', stuck);
    }
    function schedule() { if (!ticking) { ticking = true; window.requestAnimationFrame(update); } }
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    update();
  }

  // Shopify Inbox's chat button sits at a fixed --shopify-chat-activator-offset (theme setting, 120px)
  // on every page, so on phone product pages it covered the 100g size pill in the sticky Add to Bag
  // bar. While that bar is showing, lift the button just above it; otherwise leave the setting alone.
  function initChatAboveStickyBuy() {
    var root = doc.documentElement;
    function sync() {
      var bar = doc.querySelector('.mums-stickybuy');
      var h = bar && bar.offsetHeight > 0 && getComputedStyle(bar).display !== 'none' ? bar.offsetHeight : 0;
      if (h) root.style.setProperty('--shopify-chat-activator-offset', (h + 12) + 'px');
      else root.style.removeProperty('--shopify-chat-activator-offset');
    }
    sync();
    window.addEventListener('resize', sync, { passive: true });
    if ('MutationObserver' in window) {   // drawers toggle body classes that hide the bar
      new MutationObserver(sync).observe(doc.body, { attributes: true, attributeFilter: ['class'] });
    }
    setTimeout(sync, 1500);                // the bar is injected after load
  }

  onReady(function () {
    try { initStickyBarFlag(); } catch (e) {}
    try { if (doc.body.classList.contains('template-product')) initChatAboveStickyBuy(); } catch (e) {}
    try { initCartUpsell(); } catch (e) {}
    try { initQuickView(); } catch (e) {}
    try { initPicksAdd(); } catch (e) {}
    var drawer = doc.querySelector('cart-drawer') || doc.getElementById('CartDrawer');
    if (drawer && 'MutationObserver' in window) {
      var ut;
      new MutationObserver(function () {
        clearTimeout(ut);
        ut = setTimeout(function () { try { initCartUpsell(); } catch (e) {} }, 80);
      }).observe(drawer, { childList: true, subtree: true });
    }
  });
})();
